import { z } from "zod";

/**
 * Фотография решения, приложенная учеником ко ВСЕЙ домашней работе.
 * Проект целиком: `docs/homework-photo-design.md`.
 *
 * Здесь живут числа и форма строки, то есть ровно то, что обязаны одинаково
 * знать обе стороны. Отдельным файлом от `./board-image.ts` по той же причине,
 * по которой тот отделён от `./media.ts`: правила у фотографии свои (предел на
 * работу вместо счётчика за урок, отметка просмотра, видимая одному
 * преподавателю), и складывать их в чужой контракт значит однажды перепутать
 * потолки.
 *
 * ⚠️ **Второй реализации ужатия нет.** Кодированием занимается тот же
 * `board-image-encode.ts` в вебе, ему передаются числа отсюда параметром
 * (раздел 7.1 проекта). Разъехавшиеся числа означали бы фотографию, принятую
 * браузером и отвергнутую сервером уже ПОСЛЕ отправки по мобильному каналу.
 */

/**
 * Сколько фотографий влезает в одну РАБОТУ по умолчанию.
 *
 * ⚠️ **Десять, а не три, и это следствие переезда точки привязки** (решение
 * владельца 05.09.2026, редакция 2 проекта). Три на задание и три на всю работу
 * это разные величины: заданий в работе бывает и двадцать, поэтому оставленная
 * тройка молча ужала бы фичу в несколько раз, а выглядело бы это как «кнопка
 * почему-то пропала после третьего снимка». Десять это тетрадный разворот на
 * работу средней длины и всё ещё потолок, а не рабочее число.
 *
 * ⚠️ **Веб этой константы не знает и знать не должен** (раздел 3 проекта).
 * Число приезжает клиенту полем `photoLimit` в карточке работы, потому что
 * однажды оно станет зависеть от преподавателя, а зашитое в бандл оно
 * потребовало бы выкатки двух приложений вместо одного. Читает её ровно один
 * потребитель, `photoLimitFor` в `apps/api/src/repo/homework-photos.ts`.
 */
export const HOMEWORK_PHOTO_LIMIT_DEFAULT = 10;

/**
 * До скольких пикселей по длинной стороне ужимает КЛИЕНТ.
 *
 * Меньше, чем у доски (там 2000): доску разглядывают на слайде и в неё
 * всматриваются, а фотографию тетради читают целиком, и почерк на 1600 px
 * читается. Лишний мегабайт здесь оплачивается минутой ожидания школьника на
 * мобильном канале (раздел 7 проекта).
 */
export const HOMEWORK_PHOTO_MAX_EDGE = 1600;

/**
 * Потолок на то, что приезжает на сервер. Страховка, а не рычаг: после ужатия
 * сюда не влезает только ненормальное.
 *
 * Вчетверо меньше доски, и это то же решение, что у длинной стороны: 800 КБ на
 * 128 кбит/с это около пятидесяти секунд, а два мегабайта это две минуты, и
 * второе ученик просто не дождётся.
 */
export const HOMEWORK_PHOTO_MAX_BYTES = 800 * 1024;

/**
 * Сколько загрузок в минуту принимаем от одной сессии.
 *
 * ⚠️ У существующих загрузок (доска урока, доска заготовки) лимита частоты нет
 * ни у одной, и здесь он нужен заметнее всего: это единственная загрузка,
 * которую делает ученик со своего телефона, и единственная, у которой нет ни
 * счётчика за занятие, ни владельца-преподавателя рядом.
 *
 * ⚠️ **Считается ОТ предела работы, а не пишется числом, и обязан быть строго
 * больше него.** Пока предел был три на задание, разница жила сама собой;
 * 05.09.2026 предел стал десятью на работу, и записанная руками десятка тут же
 * совпала с ним. Совпадение ломает две законные последовательности внутри одной
 * минуты: заполнить работу целиком и получить на следующем нажатии честное
 * «мест больше нет», и убрать один снимок, чтобы поставить другой. В обеих
 * ученик получал «слишком быстро», то есть отказ, который врёт про причину.
 * Поймано тестом на базе в тот же час. Вдвое это запас, а не расчёт: важно
 * только то, что лимит частоты не стоит на пути разрешённого.
 */
export const HOMEWORK_PHOTO_RATE_MAX = HOMEWORK_PHOTO_LIMIT_DEFAULT * 2;

/**
 * Одна приложенная фотография в карточке работы. Задания в ней нет: с
 * 05.09.2026 фотография привязана ко всей работе (редакция 2 проекта).
 *
 * ⚠️ **`viewedAt` едет ТОЛЬКО преподавателю, и не как `null`, а отсутствием
 * поля** (раздел 4, условие 1 проекта). «Преподаватель не смотрел вашу работу»
 * это счёт в его адрес, запрещённый решением владельца 23.08.2026, и здесь он
 * худшего сорта, потому что его прочитал бы платящий родитель. Поэтому поле
 * необязательное, а собирается строка ровно в одном месте, `photoView` в
 * `apps/api/src/repo/homework-photos.ts`, и стережётся тестом на базе.
 */
export const HomeworkPhoto = z.object({
  fileId: z.string().uuid(),
  createdAt: z.number().int(),
  /** Размер снимка после ужатия. `null` у файлов, залитых мимо нашего клиента. */
  width: z.number().int().positive().nullable().default(null),
  height: z.number().int().positive().nullable().default(null),
  /**
   * Преподаватель удалил файл из медиатеки. Показываем это надписью, а не
   * пустым прямоугольником (раздел 8 проекта): поведение то же, что у картинки
   * с доски.
   */
  deleted: z.boolean().default(false),
  /** ⚠️ Только преподавателю. Отсутствие поля это «смотрит не преподаватель». */
  viewedAt: z.number().int().nullable().optional(),
});
export type HomeworkPhoto = z.infer<typeof HomeworkPhoto>;

/** Ответ узкой ручки загрузки: ровно приложенная строка. */
export const HomeworkPhotoResponse = z.object({ photo: HomeworkPhoto });
export type HomeworkPhotoResponse = z.infer<typeof HomeworkPhotoResponse>;

/**
 * Ответ отметки просмотра. Время возвращается затем, что отметка ставится один
 * раз и не двигается: у повторного открытия приедет уже записанное, и экран
 * обязан показать именно его, а не момент нажатия.
 */
export const HomeworkPhotoViewedResponse = z.object({ viewedAt: z.number().int() });
export type HomeworkPhotoViewedResponse = z.infer<typeof HomeworkPhotoViewedResponse>;
